import React, { useContext, useMemo, useState } from "react";
import {
    Alert,
    Autocomplete,
    Box,
    Checkbox,
    Stack,
    TextField,
    Typography,
    createFilterOptions,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { useQuery } from "react-query";

import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

import PageHeader from "../../components/PageHeader";
import SectionCard from "../../components/SectionCard";
import DataTable from "../../components/DataTable";
import { LanguageContext } from "../../components/LanguageContext";
import { getCalculations, getReports } from "../../api/calculationApi";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const fmtDate = (s) => {
    if (!s) return "";
    const d = new Date(s);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
};

const ReportsPage = () => {
    const { storeCode } = useParams();
    const { translations } = useContext(LanguageContext) || {};

    const [calculation, setCalculation] = useState([]);

    const { data: calculations, isLoading: isLoadingCalculations } = useQuery({
        queryKey: ["calculations", storeCode],
        queryFn: () => getCalculations(storeCode),
    });

    const { data: reports, isLoading: loadingReports } = useQuery({
        queryKey: ["reports", calculation],
        queryFn: () => getReports(calculation),
        enabled: calculation.length > 0,
    });

    const columns = useMemo(
        () => [
            {
                field: "id",
                headerName: translations?.adminSingleStorePageId || "ID",
                width: 70,
                align: "center",
                headerAlign: "center",
            },
            {
                field: "supplier_name",
                headerName:
                    translations?.poslovodjaProizvodiSupplierName ||
                    "Dobavljač",
                minWidth: 200,
                flex: 1,
            },
            {
                field: "name",
                headerName:
                    translations?.poslovodjaAcquisitionPageColumnProductName ||
                    "Proizvod",
                minWidth: 200,
                flex: 1,
            },
            {
                field: "code",
                headerName:
                    translations?.poslovodjaAcquisitionPageColumnProductCode ||
                    "Šifra",
                minWidth: 120,
                renderCell: (p) => (
                    <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                    >
                        {p.value}
                    </Typography>
                ),
            },
            {
                field: "amount_to_get",
                headerName:
                    translations?.adminAcquisitionPageColumnToAcquire ||
                    "Za nabavku",
                minWidth: 120,
                align: "center",
                headerAlign: "center",
            },
            {
                field: "hand_amount",
                headerName:
                    translations?.adminAcquisitionPageColumnManualInputAmount ||
                    "Ručno",
                minWidth: 120,
                align: "center",
                headerAlign: "center",
            },
            {
                field: "difference",
                headerName: translations?.difference || "Razlika",
                minWidth: 120,
                align: "center",
                headerAlign: "center",
            },
            {
                field: "percentage_difference",
                headerName: translations?.percentage_difference || "% Razlika",
                minWidth: 120,
                align: "center",
                headerAlign: "center",
            },
        ],
        [translations]
    );

    const storeOptions = calculations?.data || [];

    return (
        <Box>
            <PageHeader
                icon={<AssessmentOutlinedIcon />}
                eyebrow={translations?.reports || "Izveštaji"}
                title={translations?.reports || "Izveštaji"}
                description={
                    translations?.reportsSubtitle ||
                    "Izaberite kalkulacije za pregled odstupanja predloga i ručnih količina."
                }
            />

            <Stack spacing={2}>
                <SectionCard>
                    <Autocomplete
                        multiple
                        value={calculation}
                        filterOptions={(options, params) => {
                            const filter = createFilterOptions();
                            const filtered = filter(options, params);
                            return [
                                {
                                    id: translations?.chooseAll ||
                                        "Izaberi sve",
                                    s_name: "",
                                    date_time: "",
                                    all: true,
                                },
                                ...filtered,
                            ];
                        }}
                        onChange={(event, newValue) => {
                            if (newValue.find((option) => option.all)) {
                                setCalculation(
                                    calculation.length === storeOptions.length
                                        ? []
                                        : storeOptions
                                );
                                return;
                            }
                            setCalculation(newValue);
                        }}
                        options={isLoadingCalculations ? [] : storeOptions}
                        loading={isLoadingCalculations}
                        disableCloseOnSelect
                        getOptionLabel={(option) =>
                            `${option.id} ${option.s_name} ${fmtDate(
                                option.date_time
                            )}`.trim()
                        }
                        renderOption={(props, option, { selected }) => (
                            <li {...props}>
                                <Checkbox
                                    icon={icon}
                                    checkedIcon={checkedIcon}
                                    style={{ marginRight: 8 }}
                                    checked={
                                        option.all
                                            ? calculation.length ===
                                              storeOptions.length
                                            : selected
                                    }
                                />
                                {option.id} {option.s_name}{" "}
                                {fmtDate(option.date_time)}
                            </li>
                        )}
                        fullWidth
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    translations?.chooseCalculations ||
                                    "Izaberi kalkulacije"
                                }
                                placeholder={
                                    translations?.chooseCalculations ||
                                    "Izaberi kalkulacije"
                                }
                            />
                        )}
                    />
                </SectionCard>

                <SectionCard padded={false}>
                    {calculation.length === 0 ? (
                        <Alert severity="info" sx={{ m: 3 }}>
                            {translations?.errorMessageReports ||
                                "Izaberi bar jednu kalkulaciju za prikaz izveštaja."}
                        </Alert>
                    ) : (
                        <DataTable
                            rows={reports?.data || []}
                            columns={columns}
                            loading={loadingReports}
                            rowHeight={56}
                            emptyIcon={<AssessmentOutlinedIcon />}
                            emptyTitle={
                                translations?.noReportsTitle ||
                                "Nema podataka za izabrane kalkulacije"
                            }
                        />
                    )}
                </SectionCard>
            </Stack>
        </Box>
    );
};

export default ReportsPage;